"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = require("lodash");
const util_1 = require("../util");
const { GET, POST, PATCH } = util_1.httpMethods;
/**
 * @classdesc Represents the class interfacing node-service-issues related API-I endpoints
 * @class
 */
class NodeServiceIssues {
    constructor(api) {
        this.api = api;
    }
    /**
     * Get service issues according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-service-issues-service-issues-through-noc2-get}
     * @param {Object}    args={} - An object containing the arguments accepted by the API
     * @param {?string[]} args.entityIdentifiers - Optional. list of MAC / DN / LN / External node id or External serial number
     * @param {?number[]} args.propertyIds - Optional. list of properties ids
     * @param {?number[]} args.serviceRegionIds - Optional. list of service region ids
     * @param {?number[]} args.serviceZoneIds - Optional. list of service zone ids
     * @param {?string[]} args.locations - Optional. list of locations
     * @param {?string[]} args.managementTypes - Optional. list of management types
     * @param {?string[]} args.nodeTypes - Optional. list of node types
     * @param {?number[]} args.issueTypeIds - Optional. list of issue type ids
     * @param {?string[]} args.issueStatuses - Optional. list of issue statuses
     * @param {?string}   args.repairDueFrom - Optional. repair due from date
     * @param {?string}   args.repairDueTo - Optional. repair due to date
     * @param {?string[]} args.sources - Optional. list of sources
     * @param {?string}   args.deviceIssues - Optional. whether to exclude device issues. 'exclude' or 'include'
     * @param {?string}   args.testIssues - Optional. whether to exclude test issues. 'exclude' or 'include'
     * @param {?boolean}  args.autoCreatedIssues - Optional. exclude automated issues. 'exclude' or 'include'
     * @param {?number[]} args.remoteResolutionActionIds - Optional. list of remote resolution action ids
     * @param {!string}   args.reportedFrom - Required. reported from date
     * @param {?string}   args.operationStatus - Optional. operation status
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an array of data or
     * an error with the problem.
     */
    async getNodeServiceIssues(args) {
        const { entityIdentifiers, propertyIds, serviceRegionIds, serviceZoneIds, locations, managementTypes, nodeTypes, issueTypeIds, issueStatuses, repairDueFrom, repairDueTo, sources, deviceIssues, testIssues, autoCreatedIssues, remoteResolutionActionIds, reportedFrom, operationStatus, } = args || {};
        const methodName = 'getNodeServiceIssues';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'entityIdentifiers', entityIdentifiers, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'propertyIds', propertyIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'serviceRegionIds', serviceRegionIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'serviceZoneIds', serviceZoneIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'locations', locations, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'managementTypes', managementTypes, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'nodeTypes', nodeTypes, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'issueTypeIds', issueTypeIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'issueStatuses', issueStatuses, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'repairDueFrom', repairDueFrom, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'repairDueTo', repairDueTo, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'sources', sources, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'deviceIssues', deviceIssues, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'testIssues', testIssues, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'autoCreatedIssues', autoCreatedIssues, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'remoteResolutionActionIds', remoteResolutionActionIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'reportedFrom', reportedFrom, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'reportedFrom', reportedFrom, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'operationStatus', operationStatus, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(promises);
        const route = '/nodes/service_issues';
        const input = {
            query: {
                entityIdentifiers: entityIdentifiers?.join(),
                propertyIds: propertyIds?.join(),
                serviceRegionIds: serviceRegionIds?.join(),
                serviceZoneIds: serviceZoneIds?.join(),
                locations: locations?.join(),
                managementTypes: managementTypes?.join(),
                nodeTypes: nodeTypes?.join(),
                issueTypeIds: issueTypeIds?.join(),
                issueStatuses: issueStatuses?.join(),
                repairDueFrom,
                repairDueTo,
                sources: sources?.join(),
                deviceIssues,
                testIssues,
                autoCreatedIssues,
                remoteResolutionActionIds: remoteResolutionActionIds?.join(),
                reportedFrom,
                operationStatus,
            },
        };
        const formattedInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(GET, route, formattedInput);
    }
    async getServiceIssueById(args) {
        const { serviceIssueId } = args || {};
        const methodName = 'getServiceIssueById';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'serviceIssueId', serviceIssueId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'serviceIssueId', serviceIssueId, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(promises);
        const route = `/nodes/service_issue/service_issue_id/${serviceIssueId}`;
        return this.api.send(GET, route);
    }
    /**
     * Get the resolution actions for a particular node issue type id.
     * These resolution actions are used to describe the resolution attempts for the issue.
     * The data returned is in the structure of RESOLUTION TYPE > RESOLUTION ACTIONS
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#node-service-issues-get-resolution-actions-get}
     * @param {Object}  args={} - An object containing the arguments accepted by the API
     * @param {!number} args.nodeIssueTypeId - Required. issue type id
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an array of data or
     * an error with the problem.
     */
    async getIssueTypeResolutionActions(args) {
        const { nodeIssueTypeId } = args || {};
        const methodName = 'getIssueTypeResolutionActions';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'nodeIssueTypeId', nodeIssueTypeId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIssueTypeId', nodeIssueTypeId, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(promises);
        const route = `/nodes/service_issues/issue_type/${nodeIssueTypeId}/resolution_actions`;
        return this.api.send(GET, route);
    }
    /**
     * Add resolution actions to a service issue
     *
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#node-service-issues-add-resolution-actions-post}
     * @param {Object}  args={} - An object containing the arguments accepted by the API
     * @return {promise}
     */
    async addResolutionActions(args) {
        const { serviceIssueId, resolutionActionIds, resolutionDescription, imageUrls, customerId } = args || {};
        const methodName = 'addResolutionActions';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'serviceIssueId', serviceIssueId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'serviceIssueId', serviceIssueId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'resolutionActionIds', resolutionActionIds, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'resolutionDescription', resolutionDescription, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(promises);
        const route = '/nodes/service_issue/resolution_actions';
        const input = {
            header: {
                customerId,
            },
            body: args,
        };
        return this.api.send(POST, route, (0, util_1.setFormattedInput)(input));
    }
    /**
     * Update the status of a service issue
     *
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#node-service-issues-update-issue-status-patch}
     * @param {Object}  args={} - An object containing the arguments accepted by the API
     * @return {promise}
     */
    async updateIssueStatus(args) {
        const { serviceIssueId, status, customerId } = args || {};
        const methodName = 'updateIssueStatus';
        // validate
        const promises = [
            (0, util_1.validateParam)(methodName, 'serviceIssueId', serviceIssueId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'serviceIssueId', serviceIssueId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'status', status, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'status', status, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(promises);
        const route = `/nodes/service_issue/${serviceIssueId}/${status}`;
        const input = {
            header: {
                customerId,
            },
        };
        return this.api.send(PATCH, route, (0, util_1.setFormattedInput)(input));
    }
    /**
     * Get a list of node issue types
     * @see {@link https://noc2-stage.semios.com/#node-service-issues-get-issue-types-with-groups-get}
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an object of data or an error with the problem
     */
    async getNodeIssueTypes() {
        const route = '/nodes/service_issues/issue_types';
        const response = await this.api.send(GET, route);
        // Transform the response to have lower_snake_case keys for node types and UPPER_SNAKE_CASE keys for issue groups
        return Object.keys(response).reduce((acc, nodeType) => {
            acc[(0, lodash_1.snakeCase)(nodeType)] = (0, lodash_1.mapKeys)(response[nodeType], (value, key) => (0, lodash_1.snakeCase)(key).toUpperCase());
            return acc;
        }, {});
    }
}
exports.default = NodeServiceIssues;
//# sourceMappingURL=node-service-issues.js.map